import type { SeedingComment } from './pick-comments';

/**
 * Картинки для карточек комментариев TikTok: аватар автора, фото или стикер
 * под текстом и фирменные эмодзи. Плюс шрифт TikTok Sans — карточка рисуется
 * им, и он должен быть загружен до первого fillText.
 *
 * Аватар, фото и стикер лежат на CDN TikTok по подписанным адресам и без CORS:
 * холст такую картинку нарисует, но потом не отдаст toDataURL. Поэтому их, как
 * и обложки, перезаливает бэкенд (/api/reports/image-urls), а сюда приходит
 * адрес хранилища, с которого браузеру читать можно.
 *
 * Эмодзи — свои файлы в public/tiktok-emoji: каталог TikTok отдаёт их только
 * вошедшим и по адресам, которые живут 15 дней, поэтому картинки скачаны
 * один раз (29.10.2026, каталог office_emojis_v2_20261023).
 *
 * Только для комментариев, уже отобранных в отчёт: их там по три на ролик.
 * Что не загрузилось — карточка обходится без него: силуэт вместо аватара,
 * подпись «[Лайк]» вместо эмодзи, текст без фото.
 */

export type CommentImages = {
  avatar?: ImageBitmap;
  /** Фото или стикер под текстом. */
  attachment?: ImageBitmap;
  /** emoji_id → картинка. */
  emojis?: Map<string, ImageBitmap>;
};

export type CommentImagesDeps = {
  fetchImageUrls: (items: Array<{ id: string; thumbnailUrl: string }>) => Promise<Map<string, string>>;
  loadImage?: (url: string) => Promise<ImageBitmap>;
  loadFont?: () => Promise<void>;
};

const EMOJI_BASE = '/tiktok-emoji/';
const FONT_BASE = '/fonts/tiktok-sans/';

/** Наборы знаков шрифта: в cyrillic-ext лежат казахские ә, ғ, қ, ң, ө, ұ, ү, һ. */
const FONT_FILES = [
  ['latin', 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD'],
  ['latin-ext', 'U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF'],
  ['cyrillic', 'U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116'],
  ['cyrillic-ext', 'U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F'],
];

let fontReady: Promise<void> | null = null;

/** TikTok Sans (OFL, github.com/tiktok/TikTokSans) — переменный, один файл на все толщины. */
export function loadTikTokFont(): Promise<void> {
  if (fontReady) return fontReady;
  fontReady = (async () => {
    if (typeof FontFace === 'undefined' || typeof document === 'undefined') return;
    const faces = FONT_FILES.map(
      ([file, unicodeRange]) =>
        new FontFace('TikTok Sans', `url(${FONT_BASE}${file}.woff2) format('woff2')`, {
          weight: '300 900',
          unicodeRange,
        }),
    );
    await Promise.all(
      faces.map((face) =>
        face.load().then(
          (loaded) => document.fonts.add(loaded),
          (err) => console.warn('[Комментарии] шрифт TikTok Sans не загрузился:', err),
        ),
      ),
    );
  })();
  return fontReady;
}

async function loadBitmap(url: string): Promise<ImageBitmap> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return createImageBitmap(await res.blob());
}

/** Ключи в общем кэше перезаливок: аватар один на автора, вложение — на комментарий. */
const avatarKey = (c: SeedingComment) => `tt-avatar:${c.username}`;
const attachmentKey = (c: SeedingComment) => `tt-attach:${c.id}`;

export async function loadCommentImages(
  comments: SeedingComment[],
  deps: CommentImagesDeps,
): Promise<Map<string, CommentImages>> {
  const wanted = comments.filter((c) => c.platform === 'tiktok');
  const result = new Map<string, CommentImages>();
  if (wanted.length === 0) return result;

  const load = deps.loadImage ?? loadBitmap;
  const font = (deps.loadFont ?? loadTikTokFont)().catch(() => {});

  const sources = new Map<string, string>();
  for (const c of wanted) {
    if (c.avatarUrl) sources.set(avatarKey(c), c.avatarUrl);
    const attachment = c.imageUrl || c.stickerUrl;
    if (attachment) sources.set(attachmentKey(c), attachment);
  }
  let urls = new Map<string, string>();
  if (sources.size > 0) {
    try {
      urls = await deps.fetchImageUrls([...sources].map(([id, thumbnailUrl]) => ({ id, thumbnailUrl })));
    } catch (err) {
      console.warn('[Комментарии] картинки не перезалились:', err);
    }
  }

  // Одна картинка — одна загрузка, сколько бы комментариев на неё ни ссылалось.
  const cache = new Map<string, Promise<ImageBitmap | null>>();
  const once = (url: string | undefined) => {
    if (!url) return Promise.resolve(null);
    if (!cache.has(url)) cache.set(url, load(url).catch(() => null));
    return cache.get(url) as Promise<ImageBitmap | null>;
  };

  await Promise.all(
    wanted.map(async (c) => {
      const images: CommentImages = {};
      const [avatar, attachment] = await Promise.all([once(urls.get(avatarKey(c))), once(urls.get(attachmentKey(c)))]);
      if (avatar) images.avatar = avatar;
      if (attachment) images.attachment = attachment;
      if (c.emojis?.length) {
        const emojis = new Map<string, ImageBitmap>();
        for (const id of new Set(c.emojis.map((e) => e.id))) {
          const bitmap = await once(`${EMOJI_BASE}${encodeURIComponent(id)}.png`);
          if (bitmap) emojis.set(id, bitmap);
        }
        images.emojis = emojis;
      }
      result.set(c.id, images);
    }),
  );
  await font;
  return result;
}
